import React, { useState, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/Toast';
import { 
  Upload, 
  Database, 
  Table2, 
  ArrowRight, 
  Check, 
  X, 
  RefreshCw,
  FileUp,
  MapPin,
  Package,
  AlertCircle,
  CheckCircle,
  Eye
} from 'lucide-react';

const API_URL = BACKEND_URL;

// Campos do produto no SaaS
const PRODUCT_FIELDS = [
  { id: 'name', label: 'Nome do Produto', required: true },
  { id: 'barcode', label: 'Código de Barras', required: false },
  { id: 'price', label: 'Preço de Venda', required: true },
  { id: 'cost_price', label: 'Preço de Custo', required: false },
  { id: 'stock', label: 'Estoque', required: false },
  { id: 'category', label: 'Categoria', required: false },
  { id: 'description', label: 'Descrição', required: false },
  { id: 'unit', label: 'Unidade (UN, KG, etc)', required: false },
  { id: 'ncm', label: 'NCM', required: false },
  { id: 'preco_atacado', label: 'Preço Atacado', required: false },
  { id: 'preco_noturno', label: 'Preço Noturno', required: false },
];

export const FirebirdMigrationPage = () => {
  const { tenant } = useAuth();
  const toast = useToast();
  
  // Estado do fluxo
  const [step, setStep] = useState(1); // 1: Upload, 2: Selecionar Tabela, 3: Mapear, 4: Importar
  const [loading, setLoading] = useState(false);
  
  // Dados do banco
  const [sessionId, setSessionId] = useState(null);
  const [dbPassword, setDbPassword] = useState('masterkey');
  const [dbInfo, setDbInfo] = useState(null);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);
  const [sampleData, setSampleData] = useState([]);
  
  // Mapeamento
  const [fieldMapping, setFieldMapping] = useState({});
  
  // Resultado da importação
  const [importResult, setImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState(0);

  // Step 1: Upload do arquivo
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadError, setUploadError] = useState('');
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.gdb') && !file.name.toLowerCase().endsWith('.fdb')) {
      toast.error('Arquivo deve ser .gdb ou .fdb');
      return;
    }
    
    // Verificar tamanho (máximo 500MB)
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Arquivo muito grande. Máximo permitido: 500MB');
      return;
    }
    
    setLoading(true);
    setUploadProgress(0);
    setUploadError('');
    setUploadStatus(`Enviando ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)...`);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('password', dbPassword);
      
      const token = localStorage.getItem('token');
      
      // Usar XMLHttpRequest para ter progresso do upload
      const xhr = new XMLHttpRequest();
      
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(percent);
            if (percent < 100) {
              setUploadStatus(`Enviando... ${percent}%`);
            } else {
              setUploadStatus('Upload completo! Processando banco de dados...');
            }
          }
        });
        
        xhr.addEventListener('load', () => {
          console.log('XHR Load - Status:', xhr.status, 'Response:', xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (e) {
              console.error('Erro ao parsear resposta:', e);
              reject(new Error('Resposta inválida do servidor'));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              console.error('Erro do servidor:', errorData);
              reject(new Error(errorData.message || errorData.detail || `Erro ${xhr.status}`));
            } catch (e) {
              reject(new Error(`Erro ${xhr.status}: ${xhr.statusText}`));
            }
          }
        });
        
        xhr.addEventListener('error', (e) => {
          console.error('XHR Error:', e);
          reject(new Error('Erro de conexão. Verifique sua internet.'));
        });
        
        xhr.addEventListener('timeout', () => {
          reject(new Error('Timeout: O arquivo pode ser muito grande. Tente novamente.'));
        });
        
        xhr.open('POST', `${API_URL}/api/migrate/firebird/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.timeout = 300000; // 5 minutos de timeout
        xhr.send(formData);
      });
      
      const data = await uploadPromise;
      console.log('Upload response:', data);
      
      if (data.success) {
        setSessionId(data.session_id);
        setDbInfo(data.database_info);
        setTables(data.database_info?.product_tables || []);
        toast.success(`Banco carregado! ${data.database_info?.tables_count || 0} tabelas encontradas.`);
        setStep(2);
      } else {
        const errorMsg = data.message || 'Erro ao carregar banco. Verifique a senha.';
        setUploadError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      const errorMsg = error.message || 'Erro ao enviar arquivo. Verifique a conexão.';
      setUploadError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      setUploadProgress(0);
      setUploadStatus('');
    }
  };

  // Step 2: Selecionar tabela e carregar colunas
  const selectTable = async (tableName) => {
    setLoading(true);
    setSelectedTable(tableName);
    
    try {
      const token = localStorage.getItem('token');
      
      // Buscar colunas
      const columnsResponse = await fetch(
        `${API_URL}/api/migrate/firebird/columns?session_id=${sessionId}&table_name=${tableName}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const columnsData = await columnsResponse.json();
      
      if (columnsData.success) {
        setTableColumns(columnsData.columns || []);
      }
      
      // Buscar amostra de dados
      const sampleResponse = await fetch(
        `${API_URL}/api/migrate/firebird/sample?session_id=${sessionId}&table_name=${tableName}&limit=5`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const sampleDataResult = await sampleResponse.json();
      
      if (sampleDataResult.success) {
        setSampleData(sampleDataResult.data || []);
      }
      
      // Inicializar mapeamento automático
      const autoMapping = {};
      const lowerColumns = (columnsData.columns || []).map(c => ({
        ...c,
        lower: c.name.toLowerCase()
      }));
      
      // Tentar mapear automaticamente
      lowerColumns.forEach(col => {
        const name = col.lower;
        if (name.includes('descri') || name.includes('nome') || name === 'name') {
          autoMapping[col.name] = 'name';
        } else if (name.includes('preco') || name.includes('valor') || name.includes('price')) {
          if (name.includes('custo') || name.includes('cost')) {
            autoMapping[col.name] = 'cost_price';
          } else if (name.includes('atacado')) {
            autoMapping[col.name] = 'preco_atacado';
          } else {
            autoMapping[col.name] = 'price';
          }
        } else if (name.includes('barra') || name.includes('ean') || name.includes('gtin') || name.includes('codigo')) {
          autoMapping[col.name] = 'barcode';
        } else if (name.includes('estoque') || name.includes('qtd') || name.includes('quant') || name.includes('stock')) {
          autoMapping[col.name] = 'stock';
        } else if (name.includes('categ') || name.includes('grupo')) {
          autoMapping[col.name] = 'category';
        } else if (name.includes('unid')) {
          autoMapping[col.name] = 'unit';
        } else if (name.includes('ncm')) {
          autoMapping[col.name] = 'ncm';
        }
      });
      
      setFieldMapping(autoMapping);
      setStep(3);
      
    } catch (error) {
      console.error('Erro ao carregar tabela:', error);
      toast.error('Erro ao carregar dados da tabela');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Atualizar mapeamento
  const updateMapping = (sourceField, targetField) => {
    setFieldMapping(prev => {
      const newMapping = { ...prev };
      
      // Remover mapeamento anterior para o mesmo target
      Object.keys(newMapping).forEach(key => {
        if (newMapping[key] === targetField) {
          delete newMapping[key];
        }
      });
      
      if (targetField && targetField !== '_none') {
        newMapping[sourceField] = targetField;
      } else {
        delete newMapping[sourceField];
      }
      
      return newMapping;
    });
  };

  // Step 4: Executar importação
  const executeImport = async () => {
    // Verificar campos obrigatórios
    const mappedTargets = Object.values(fieldMapping);
    if (!mappedTargets.includes('name')) {
      toast.error('Campo "Nome do Produto" é obrigatório');
      return;
    }
    if (!mappedTargets.includes('price')) {
      toast.error('Campo "Preço de Venda" é obrigatório');
      return;
    }
    
    setLoading(true);
    setImportProgress(10);
    setStep(4);
    
    try {
      const token = localStorage.getItem('token');
      
      setImportProgress(30);
      
      const response = await fetch(`${API_URL}/api/migrate/firebird/import`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          table_name: selectedTable,
          field_mapping: fieldMapping
        })
      });
      
      setImportProgress(70);
      
      const data = await response.json();
      
      setImportProgress(100);
      
      if (data.success) {
        setImportResult(data);
        toast.success(`Importação concluída! ${data.imported} produtos importados.`);
      } else {
        toast.error(data.message || 'Erro na importação');
        setImportResult({ success: false, error: data.message });
      }
    } catch (error) {
      console.error('Erro na importação:', error);
      toast.error('Erro ao importar dados');
      setImportResult({ success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Reiniciar processo
  const restart = () => {
    setStep(1);
    setSessionId(null);
    setDbInfo(null);
    setTables([]);
    setSelectedTable(null);
    setTableColumns([]);
    setSampleData([]);
    setFieldMapping({});
    setImportResult(null);
    setImportProgress(0);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Database className="w-6 h-6" />
          Migração de Dados - Firebird
        </h1>
        <p className="text-slate-500">
          Importe produtos do seu banco de dados Firebird (.gdb/.fdb) para o sistema
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {[
          { num: 1, label: 'Upload' },
          { num: 2, label: 'Selecionar Tabela' },
          { num: 3, label: 'Mapear Campos' },
          { num: 4, label: 'Importar' }
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step > s.num ? 'bg-green-500 text-white' :
              step === s.num ? 'bg-blue-500 text-white' :
              'bg-slate-200 text-slate-500'
            }`}>
              {step > s.num ? <Check className="w-5 h-5" /> : s.num}
            </div>
            <span className={`ml-2 ${step >= s.num ? 'text-slate-900' : 'text-slate-400'}`}>
              {s.label}
            </span>
            {idx < 3 && (
              <div className={`w-16 h-1 mx-4 ${step > s.num ? 'bg-green-500' : 'bg-slate-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Carregar Banco de Dados
            </CardTitle>
            <CardDescription>
              Selecione o arquivo .gdb ou .fdb do seu sistema antigo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="password">Senha do Banco (padrão: masterkey)</Label>
              <Input
                id="password"
                type="password"
                value={dbPassword}
                onChange={(e) => setDbPassword(e.target.value)}
                placeholder="masterkey"
                className="mt-1"
              />
            </div>
            
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <input
                type="file"
                id="file-upload"
                accept=".gdb,.fdb"
                onChange={handleFileUpload}
                className="hidden"
                disabled={loading}
              />
              <label 
                htmlFor="file-upload" 
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <FileUp className="w-12 h-12 text-slate-400" />
                <span className="text-lg font-medium">
                  {loading ? 'Carregando...' : 'Clique para selecionar o arquivo'}
                </span>
                <span className="text-sm text-slate-500">
                  Arquivos .gdb ou .fdb (Firebird)
                </span>
              </label>
            </div>
            
            {loading && (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>{uploadStatus || 'Processando arquivo...'}</span>
                </div>
                {uploadProgress > 0 && (
                  <Progress value={uploadProgress} className="h-2" />
                )}
              </div>
            )}
            
            {/* Dicas */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-800 font-medium mb-2">Dicas:</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Arquivos suportados: .gdb e .fdb (Firebird)</li>
                <li>• Senha padrão do Firebird: masterkey</li>
                <li>• Para arquivos grandes, aguarde o upload completo</li>
              </ul>
            </div>
            
            {/* Erro detalhado */}
            {uploadError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-red-800 font-medium mb-1">Erro no upload:</p>
                <p className="text-sm text-red-700">{uploadError}</p>
                <p className="text-xs text-red-600 mt-2">
                  Possíveis causas: senha incorreta, arquivo corrompido, versão incompatível do Firebird
                </p>
              </div>
            )}
            
            {/* Guia de Solução para Versão Incompatível */}
            {uploadError && uploadError.toLowerCase().includes('versão') && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mt-4">
                <p className="text-sm text-amber-800 font-bold mb-2">
                  Seu banco parece ser Firebird 2.1 ou anterior
                </p>
                <p className="text-sm text-amber-700 mb-3">
                  O sistema suporta Firebird 2.5 ou superior. Para converter seu banco:
                </p>
                <ol className="text-sm text-amber-700 space-y-2 list-decimal list-inside">
                  <li>
                    <strong>Instale o Firebird 2.5</strong> no seu computador local 
                    <a href="https://firebirdsql.org/en/firebird-2-5/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-1">
                      (download aqui)
                    </a>
                  </li>
                  <li>
                    <strong>Faça backup do banco antigo:</strong>
                    <code className="bg-amber-100 px-2 py-0.5 rounded ml-1 text-xs">
                      gbak -b seu_banco.gdb backup.fbk -user SYSDBA -password masterkey
                    </code>
                  </li>
                  <li>
                    <strong>Restaure para o novo formato:</strong>
                    <code className="bg-amber-100 px-2 py-0.5 rounded ml-1 text-xs">
                      gbak -c backup.fbk novo_banco.fdb -user SYSDBA -password masterkey
                    </code>
                  </li>
                  <li>
                    <strong>Faça o upload</strong> do arquivo <code className="bg-amber-100 px-1 rounded">novo_banco.fdb</code> aqui
                  </li>
                </ol>
                <p className="text-xs text-amber-600 mt-3">
                  Nota: Este processo converte automaticamente a estrutura do banco para uma versão compatível.
                </p>
              </div>
            )}
            
            {/* Guia geral para problemas de compatibilidade */}
            <div className="bg-slate-100 border border-slate-300 rounded-lg p-4 mt-4">
              <details>
                <summary className="text-sm font-medium text-slate-700 cursor-pointer">
                  Problemas com a migração? Veja o guia de solução
                </summary>
                <div className="mt-3 text-sm text-slate-600 space-y-3">
                  <div>
                    <p className="font-medium">Erro de versão incompatível:</p>
                    <p>Seu banco Firebird pode ser muito antigo (versão 2.1 ou anterior). 
                    Use o comando <code className="bg-slate-200 px-1 rounded">gbak</code> para converter.</p>
                  </div>
                  <div>
                    <p className="font-medium">Senha incorreta:</p>
                    <p>A senha padrão é <code className="bg-slate-200 px-1 rounded">masterkey</code>. 
                    Se foi alterada, use a senha correta.</p>
                  </div>
                  <div>
                    <p className="font-medium">Arquivo corrompido:</p>
                    <p>Tente fazer um backup/restore do banco antes de enviar.</p>
                  </div>
                </div>
              </details>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Selecionar Tabela */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Table2 className="w-5 h-5" />
              Selecionar Tabela de Produtos
            </CardTitle>
            <CardDescription>
              {dbInfo?.tables_count || 0} tabelas encontradas. Selecione a tabela que contém os produtos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Resumo do banco */}
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-slate-700">Informações do Banco:</p>
              <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                <span className="text-slate-500">Total de tabelas:</span>
                <span className="font-medium">{dbInfo?.tables_count || 0}</span>
                <span className="text-slate-500">Tabelas com dados:</span>
                <span className="font-medium">{dbInfo?.tables?.filter(t => (dbInfo.table_counts?.[t] || 0) > 0).length || 0}</span>
              </div>
            </div>
            
            {tables.length > 0 && (
              <div className="space-y-2 mb-6">
                <p className="text-sm font-medium text-green-600 mb-4">
                  Tabelas detectadas como possíveis tabelas de produtos:
                </p>
                {tables.map((table) => (
                  <div
                    key={table.name}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                    onClick={() => selectTable(table.name)}
                  >
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium">{table.name}</p>
                        <p className="text-sm text-slate-500">{table.count} registros</p>
                      </div>
                    </div>
                    <Button size="sm">
                      Selecionar
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Sempre mostrar todas as tabelas */}
            <div className="mt-4">
              <p className="text-sm font-medium text-slate-600 mb-2">
                {tables.length > 0 ? 'Ou selecione outra tabela:' : 'Selecione uma tabela:'}
              </p>
              <ScrollArea className="h-48 border rounded-lg">
                <div className="p-2 space-y-1">
                  {dbInfo?.tables?.map((tableName) => (
                    <div
                      key={tableName}
                      className="flex items-center justify-between p-3 hover:bg-slate-100 rounded cursor-pointer transition-colors"
                      onClick={() => selectTable(tableName)}
                    >
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{tableName}</span>
                      </div>
                      <Badge variant="outline">
                        {dbInfo.table_counts?.[tableName] || 0} registros
                      </Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
            
            <Button variant="outline" className="mt-4" onClick={() => setStep(1)}>
              Voltar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Mapear Campos */}
      {step === 3 && (
        <div className="grid grid-cols-2 gap-6">
          {/* Mapeamento */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Mapear Campos
              </CardTitle>
              <CardDescription>
                Tabela: {selectedTable} ({sampleData.length > 0 ? `${tableColumns.length} colunas` : 'carregando...'})
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] pr-4">
                <div className="space-y-3">
                  {tableColumns.map((column) => (
                    <div key={column.name} className="flex items-center gap-3 p-2 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{column.name}</p>
                        <p className="text-xs text-slate-500">{column.type}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <Select
                        value={fieldMapping[column.name] || '_none'}
                        onValueChange={(value) => updateMapping(column.name, value)}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">-- Não mapear --</SelectItem>
                          {PRODUCT_FIELDS.map((field) => (
                            <SelectItem key={field.id} value={field.id}>
                              {field.label} {field.required && '*'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setStep(2)}>
                  Voltar
                </Button>
                <Button 
                  className="flex-1"
                  onClick={executeImport}
                  disabled={loading || !Object.values(fieldMapping).includes('name')}
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Importar Produtos
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview dos dados */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Preview dos Dados
              </CardTitle>
              <CardDescription>
                Amostra dos primeiros registros
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {sampleData.length > 0 ? (
                  <div className="space-y-4">
                    {sampleData.map((row, idx) => (
                      <div key={idx} className="p-3 border rounded-lg bg-slate-50">
                        <p className="text-xs text-slate-500 mb-2">Registro #{idx + 1}</p>
                        {Object.entries(row).map(([key, value]) => {
                          const isMapped = fieldMapping[key];
                          return (
                            <div key={key} className={`flex justify-between text-sm py-1 ${isMapped ? 'bg-green-50 px-2 rounded' : ''}`}>
                              <span className="text-slate-500">{key}:</span>
                              <span className="font-medium truncate ml-2 max-w-[200px]">
                                {value !== null && value !== undefined ? String(value) : '-'}
                              </span>
                              {isMapped && (
                                <Badge variant="outline" className="ml-2 text-xs">
                                  → {PRODUCT_FIELDS.find(f => f.id === isMapped)?.label}
                                </Badge>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <RefreshCw className="w-8 h-8 mx-auto animate-spin mb-2" />
                    Carregando preview...
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 4: Resultado */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {importResult?.success ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : loading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-500" />
              )}
              {loading ? 'Importando...' : importResult?.success ? 'Importação Concluída!' : 'Erro na Importação'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                <Progress value={importProgress} className="h-3" />
                <p className="text-center text-slate-500">
                  Processando dados... {importProgress}%
                </p>
              </div>
            ) : importResult?.success ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">{importResult.imported}</p>
                    <p className="text-sm text-green-700">Produtos Importados</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">{importResult.updated || 0}</p>
                    <p className="text-sm text-blue-700">Produtos Atualizados</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-3xl font-bold text-red-600">{importResult.errors?.length || 0}</p>
                    <p className="text-sm text-red-700">Erros</p>
                  </div>
                </div>
                
                {importResult.errors?.length > 0 && (
                  <div className="mt-4">
                    <p className="font-medium text-red-600 mb-2">Erros encontrados:</p>
                    <ScrollArea className="h-32 border rounded-lg p-2">
                      {importResult.errors.map((err, idx) => (
                        <p key={idx} className="text-sm text-red-500">
                          {err.product}: {err.error}
                        </p>
                      ))}
                    </ScrollArea>
                  </div>
                )}
                
                <Button className="w-full" onClick={restart}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Fazer Nova Importação
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
                <p className="text-lg font-medium text-red-600">
                  {importResult?.error || 'Erro desconhecido'}
                </p>
                <Button className="mt-4" onClick={() => setStep(3)}>
                  Voltar e Tentar Novamente
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FirebirdMigrationPage;
